import { Box, makeStyles, Tab, Tabs, Typography } from "@material-ui/core";
import { MailOutline, PhoneOutlined } from "@material-ui/icons";
import dayjs from "dayjs";
import React, { FC, useState } from "react";
import { useRecoilValue } from "recoil";

import { Agent } from "@/api/generated";
import { AgentDetailHeader } from "@/components/agent/AgentDetailHeader";
import { AgentDetails } from "@/components/agent/AgentDetails";
import { CustomButton } from "@/components/Button";
import { LinkifiedText } from "@/components/LinkifiedText";
import { PLANNER_MATCH_CTA_LABEL } from "@/constants/plannerMatching";
import { layoutTokens, semanticColors as colors, textStyles } from "@/designTokens";
import { useDevice } from "@/hooks/useDevice";
import { useGetAgentDetails } from "@/hooks/useGetAgentDetails";
import { useObservedHeight } from "@/hooks/useObservedHeight";
import theme from "@/theme";
import {
  formatPreferredContactDate,
  PREFERRED_CONTACT_TIME_SLOT_LABEL_MAP,
} from "@/utilities/preferredContactSchedules";
import { UserState } from "@/view_models/atoms";

const useStyles = makeStyles({
  root: {
    background: colors["surface/card"],
    minWidth: 0,
    [theme.breakpoints.up("md")]: { borderRadius: layoutTokens.radius.lg, overflow: "clip" },
  },
  tabs: {
    borderBottom: `1px solid ${colors["border/default"]}`,
    "& .MuiTab-root": { minWidth: 0, fontSize: 14, fontWeight: 700 },
  },
  content: {
    padding: "16px 24px 32px",
    display: "flex",
    flexDirection: "column",
    gap: layoutTokens.space[24],
    [theme.breakpoints.up("md")]: { padding: layoutTokens.space[32] },
  },
  messagePlanner: {
    margin: "-16px -24px 0",
    [theme.breakpoints.up("md")]: { margin: "-32px -32px 0" },
  },
  plannerContent: { backgroundColor: colors["surface/base"], [theme.breakpoints.up("md")]: { padding: 32 } },
  plannerSchedule: { padding: "24px 24px 0", [theme.breakpoints.up("md")]: { padding: "0 0 24px" } },
  letter: {
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    ...textStyles["SP/Body/Large"],
    [theme.breakpoints.up("md")]: textStyles["PC/Body/Large"],
    "& p": { font: "inherit" },
  },
  date: {
    ...textStyles["Number/Caption"],
    color: colors["text/secondary"],
    textAlign: "right",
    marginBottom: layoutTokens.space[12],
  },
  heading: { ...textStyles["SP/Heading/H3"], color: colors["text/heading"], marginBottom: layoutTokens.space[12] },
  block: {
    background: colors["surface/base"],
    padding: layoutTokens.space[16],
    borderRadius: layoutTokens.radius.md,
    minWidth: 0,
  },
  schedule: { display: "flex", flexDirection: "column", gap: layoutTokens.space[8] },
  scheduleRow: { display: "flex", alignItems: "center", gap: layoutTokens.space[8] },
  scheduleLabel: {
    ...textStyles["SP/Body/Small"],
    color: colors["text/secondary"],
    flexShrink: 0,
    [theme.breakpoints.up("md")]: textStyles["PC/Body/Small"],
  },
  scheduleValue: {
    ...textStyles["SP/Body/Large"],
    flex: 1,
    textAlign: "right",
    overflowWrap: "anywhere",
    [theme.breakpoints.up("md")]: textStyles["PC/Body/Large"],
  },
  matchedBlocks: {
    display: "flex",
    flexDirection: "column",
    gap: layoutTokens.space[16],
    [theme.breakpoints.up("md")]: { flexDirection: "row", "& > *": { flex: 1 } },
    [theme.breakpoints.between("md", 1200)]: { flexDirection: "column" },
  },
  contact: { display: "flex", flexDirection: "column", gap: layoutTokens.space[8] },
  contactRow: {
    display: "flex",
    alignItems: "center",
    gap: layoutTokens.space[8],
    ...textStyles["SP/Body/Large"],
    overflowWrap: "anywhere",
    [theme.breakpoints.up("md")]: textStyles["PC/Body/Large"],
    "& svg": { width: 16, height: 16, color: colors["surface/trust"], flexShrink: 0 },
  },
  contactPc: { display: "none", [theme.breakpoints.up("md")]: { display: "block" } },
  cta: {
    background: colors["surface/card"],
    padding: "24px 24px 32px",
    paddingBottom: "calc(32px + env(safe-area-inset-bottom, 0px))",
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 20,
    boxShadow: theme.shadows[3],
    [theme.breakpoints.up("md")]: {
      position: "sticky",
      padding: "24px 32px 32px",
      borderTop: `1px solid ${colors["border/default"]}`,
      boxShadow: "none",
    },
  },
  ctaButton: { maxWidth: 360, margin: "0 auto", display: "flex", flexDirection: "column", gap: layoutTokens.space[8] },
  // 計測前はLボタン48px + お断りリンク40px + 間8px + 上24px + 下32pxを確保し、実寸が取れたら置き換える。
  withCta: { paddingBottom: "calc(152px + env(safe-area-inset-bottom, 0px))", [theme.breakpoints.up("md")]: { paddingBottom: 0 } },
});

export interface MessageDetailData {
  agent?: Agent;
  proposal_date?: string;
  proposal_content?: string;
  is_matched?: boolean | null;
  is_matchable?: boolean | null;
  first_preferred_contact_date?: string;
  first_preferred_contact_time_slot?: string;
  second_preferred_contact_date?: string;
  second_preferred_contact_time_slot?: string;
}

// 「話を聞いてみる」の下に出すお断りのテキストリンク（Trello #224）
export const MESSAGE_REFUSE_LABEL = "お断りする";

export const MessageDetail: FC<{
  proposalData: MessageDetailData;
  onApprove: () => void;
  approving?: boolean;
  // 省略時はお断りの導線を出さない。マッチ済みのメッセージでも出さない（マッチ後はお断りできないため）
  onRefuse?: () => void;
  refusing?: boolean;
}> = ({ proposalData: data, onApprove, approving = false, onRefuse, refusing = false }) => {
  const classes = useStyles();
  const [tabIndex, setTabIndex] = useState(0);
  const user = useRecoilValue(UserState);
  const agent = data.agent;
  const showCta = data.is_matchable !== false;
  const { isMobile } = useDevice();
  const { ref: actionsRef, height: actionsHeight } = useObservedHeight<HTMLDivElement>({ enabled: isMobile && showCta });
  const rows =
    data.first_preferred_contact_date &&
    data.first_preferred_contact_time_slot &&
    data.second_preferred_contact_date &&
    data.second_preferred_contact_time_slot
      ? [
          { label: "第1希望日", date: data.first_preferred_contact_date, time: data.first_preferred_contact_time_slot },
          {
            label: "第2希望日",
            date: data.second_preferred_contact_date,
            time: data.second_preferred_contact_time_slot,
          },
        ]
      : [];

  const renderDate = () => (
    <Typography className={classes.date}>
      {data.proposal_date ? dayjs(data.proposal_date).format("M月D日 HH:mm") : "-"}
    </Typography>
  );
  const renderSchedule = () => (
    <Box className={`${classes.block} ${classes.schedule}`}>
      <Typography component="h2" className={classes.heading}>初回ヒアリング予定</Typography>
      {rows.map((row) => (
        <Box className={classes.scheduleRow} key={row.label}>
          <Typography className={classes.scheduleLabel}>{row.label}</Typography>
          <Typography className={classes.scheduleValue}>
            {`${formatPreferredContactDate(row.date)}　${PREFERRED_CONTACT_TIME_SLOT_LABEL_MAP[row.time] ?? row.time}`}
          </Typography>
        </Box>
      ))}
    </Box>
  );
  const renderContact = (headingInside = false) => (
    <Box data-testid="offer-planner-contact">
      {!headingInside && (
        <Typography component="h2" className={classes.heading}>
          連絡先
        </Typography>
      )}
      <Box className={classes.block}>
        {headingInside && (
          <Typography component="h2" className={classes.heading}>
            連絡先
          </Typography>
        )}
        <Box className={classes.contact}>
          <Box className={classes.contactRow}>
            <PhoneOutlined />
            <span>{agent?.phone_number}</span>
          </Box>
          <Box className={classes.contactRow}>
            <MailOutline />
            <span>{agent?.email}</span>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box
      className={`${classes.root} ${showCta ? classes.withCta : ""}`}
      style={{ paddingBottom: isMobile && showCta ? actionsHeight || undefined : 0 }}
      data-testid="message-detail"
    >
      <Tabs
        value={tabIndex}
        onChange={(_, value: number) => setTabIndex(value)}
        variant="fullWidth"
        indicatorColor="primary"
        textColor="primary"
        aria-label="オファー詳細"
        className={classes.tabs}
      >
        <Tab label="メッセージ詳細" id="offer-message-tab" aria-controls="offer-message-panel" />
        <Tab label="認定プランナー情報" id="offer-planner-tab" aria-controls="offer-planner-panel" />
      </Tabs>
      {tabIndex === 0 && (
        <Box className={classes.content} role="tabpanel" id="offer-message-panel" aria-labelledby="offer-message-tab">
          {agent && (
            <Box className={classes.messagePlanner} data-testid="message-planner-summary">
              <AgentDetailHeader agent={agent} variant="planner" showBeginnerMessage={false} nameComponent="h2" />
            </Box>
          )}
          {(rows.length > 0 || data.is_matched) && (
            <Box className={classes.matchedBlocks}>
              {rows.length > 0 && renderSchedule()}
              {data.is_matched && <Box className={classes.contactPc}>{renderContact(true)}</Box>}
            </Box>
          )}
          <Box className={classes.letter} data-testid="message-letter">
            {renderDate()}
            <Typography>{`${user?.family_name ?? ""} ${user?.first_name ?? ""} 様`}</Typography>
            <br />
            <Typography>
              <LinkifiedText text={data.proposal_content} />
            </Typography>
          </Box>
        </Box>
      )}
      {tabIndex === 1 && (
        <Box className={classes.plannerContent} role="tabpanel" id="offer-planner-panel" aria-labelledby="offer-planner-tab">
          {rows.length > 0 && <Box className={classes.plannerSchedule}>{renderSchedule()}</Box>}
          {agent && (Number.isInteger(agent.id) && agent.id > 0 ? (
            <MessagePlannerDetails key={agent.id} agent={agent} isMatched={!!data.is_matched} />
          ) : <AgentDetails agent={agent} isMatched={!!data.is_matched} nameComponent="h2" />)}
        </Box>
      )}
      {showCta && (
        <div ref={actionsRef} className={classes.cta} data-testid="message-actions">
          <Box className={classes.ctaButton}>
            <CustomButton tone={tabIndex === 1 ? "trust" : "primary"} buttonSize="L" fullWidth onClick={onApprove} disabled={approving || data.is_matched || refusing}>
              {data.is_matched ? "マッチング済み" : PLANNER_MATCH_CTA_LABEL}
            </CustomButton>
            {onRefuse && !data.is_matched && (
              <CustomButton tone="text" buttonSize="M" fullWidth onClick={onRefuse} disabled={approving || refusing}>
                {MESSAGE_REFUSE_LABEL}
              </CustomButton>
            )}
          </Box>
        </div>
      )}
    </Box>
  );
};

// 情報タブを開いた時だけ通常の詳細APIを読み、最新の情報を同じ表示部品へ渡す。
const MessagePlannerDetails: FC<{ agent: Agent; isMatched: boolean }> = ({ agent: fallbackAgent, isMatched }) => {
  const { agent, loading, error, fetchAgentById } = useGetAgentDetails(fallbackAgent.id);
  const status = loading ? <Box p={3} role="status">プランナー情報を読み込んでいます</Box> : error ? (
    <Box p={3}>
      <Typography>プランナー情報を取得できませんでした。</Typography>
      <CustomButton tone="text" buttonSize="M" onClick={fetchAgentById}>再読み込み</CustomButton>
    </Box>
  ) : undefined;
  return <AgentDetails agent={agent ?? fallbackAgent} isMatched={isMatched} nameComponent="h2" status={status} />;
};
